import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

type StyleEntry =
  | SystemStyleObject<Theme>
  | ((theme: Theme) => SystemStyleObject<Theme>);

const drawerWidth = 263;
const sizePx = '4px';

const getScrollbarStyles = () => ({
  '&::-webkit-scrollbar': {
    width: sizePx,
    height: sizePx,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255, 255, 255, 0.3)',
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  scrollbarWidth: 'thin' as const,
  scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
  '&::-ms-scrollbar': {
    width: sizePx,
    height: sizePx,
  },
  '&::-ms-scrollbar-track': {
    background: 'transparent',
  },
  '&::-ms-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
  },
});

export const styles = {
  menuButton: {
    marginRight: '36px',
  },
  hide: {
    display: 'none',
  },
  leftArrow: (theme: Theme) => ({
    color: theme.palette.gray[1200],
  }),
  drawer: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: (theme: Theme) => ({
    overflow: 'hidden',
    backgroundColor: theme.palette.dark.main,
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
    },
  }),
  drawerClose: (theme: Theme) => ({
    overflow: 'hidden',
    backgroundColor: theme.palette.dark.main,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 'auto',
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(12),
    },
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
    },
  }),
  toolbar: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }),
  list: (theme: Theme) => ({
    backgroundColor: theme.palette.dark.main,
    zIndex: 15,
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: '180px',
    ...getScrollbarStyles(),
  }),
  listOpen: (theme: Theme) => ({
    height: 'calc(100vh - 180px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: theme.palette.dark.main,
    ...getScrollbarStyles(),
  }),
  listBottom: (theme: Theme) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 0,
    backgroundColor: theme.palette.dark.main,
    zIndex: 20,
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  }),
  logoAll: {
    padding: 0,
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  logo: {
    padding: 0,
    margin: '4px',
    marginBottom: 0,
    marginLeft: '8px',
  },
  logoText: {
    fontWeight: 'normal',
    fontSize: '24px',
    alignSelf: 'center',
    color: '#F4F2F9',
    padding: 0,
    marginLeft: '12px',
  },
  logout: {
    padding: 0,
    width: drawerWidth,
    '&:hover': {
      backgroundColor: '#F4F2F9',
    },
  },
  logoutLight: (theme: Theme) => ({
    padding: 0,
    width: drawerWidth,
    backgroundColor: theme.palette.dark.main,
    transition: 'auto',
    '&:hover': {
      backgroundColor: '#F4F2F9',
      '& p': {
        color: '#6D46DD',
      },
    },
  }),
  logoutLightText: {
    fontWeight: 'normal',
    fontSize: '13px',
    alignSelf: 'center',
    color: '#F4F2F9',
  },
  homeText: {
    fontWeight: 'normal',
    fontSize: '13px',
    alignSelf: 'center',
    color: '#F4F2F9',
    '&:hover': {
      color: '#6D46DD',
    },
  },
  homeTextLight: {
    fontWeight: 'normal',
    fontSize: '13px',
    alignSelf: 'center',
    color: '#6D46DD',
  },
  home: (theme: Theme) => ({
    padding: 0,
    width: drawerWidth,
    marginBottom: theme.spacing(2),
    '&:hover': {
      backgroundColor: '#F4F2F9',
    },
  }),
  homeLight: {
    padding: 0,
    width: drawerWidth,
    backgroundColor: '#F4F2F9',
    '&:hover': {
      backgroundColor: '#F4F2F9',
    },
  },
  openMenu: {
    padding: 0,
  },
  version: {
    margin: 0,
  },
  versionOpen: {
    margin: 0,
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  versionText: {
    padding: 0,
    fontSize: '11px',
    color: '#F4F2F9',
    fontWeight: 'normal',
  },
} satisfies Record<string, StyleEntry>;

export const hoverStyle: SystemStyleObject<Theme> = {
  backgroundColor: '#F4F2F9',
  zIndex: 2,
  left: '40px',
  padding: '18px 20px 18px 10px',
  color: '#6D46DD',
};

export const hoverStyleHome: SystemStyleObject<Theme> = {
  backgroundColor: '#F4F2F9',
  zIndex: 2,
  left: '40px',
  padding: '14px 20px 14px 10px',
  color: '#6D46DD',
};
